<template>
    <div id="modal-target" />
    <Toaster position="top-right" :duration="5000" theme="dark" rich-colors close-button />

    <ReauthModal v-if="ready" />
    <!-- One instance for the whole app: every locked sidebar item and tab opens this
         through the paywall store, so the three surfaces cannot describe a lock
         differently. Admin-only by construction -- see plugins/entitlement.ts. -->
    <PaywallModal v-if="ready" />
    <router-view v-show="ready" />
    <Transition name="preloader-fade">
        <Preloader v-if="!ready" :message="loadingMessage" :error="loadError" />
    </Transition>
</template>

<script lang="ts">
import { defineAsyncComponent, defineComponent, ref } from 'vue';
import { Toaster } from 'vue-sonner';
import 'vue-sonner/style.css';
import AuthenticationService from '~/api/services/client/authentication';
import SettingsService from '~/api/services/client/settings';
import { loadLanguage, Logger, Router, setCurrentLanguage } from '~/core';
import { applyThemePreview } from '~/helpers/themePreview';
import { useSettingsStore, useUserStore } from '~/stores';
import Preloader from '~/views/Preloader.vue';

const ReauthModal = defineAsyncComponent(() => import('~/views/account/ReauthModal.vue'));
const PaywallModal = defineAsyncComponent(() => import('~/components/paywall/PaywallModal.vue'));

export default defineComponent({
    components: { Toaster, Preloader, ReauthModal, PaywallModal },
    setup() {
        // We'd use <Suspense> but as this is the parent component, it won't work :(
        const ready = ref(false);
        const loadError = ref(false);
        const loadingMessage = ref('Booting');
        const userStore = useUserStore();
        const settingsStore = useSettingsStore();

        // Kick off the English locale download in parallel with the
        // user + settings API calls. Previously this ran *after* both
        // finished, adding the full locale-chunk download to the
        // critical path before the first route could render. Since
        // loadLanguage doesn't depend on user data, there's no reason
        // to sequence it.
        const languageReady = loadLanguage('en');

        Promise.all([AuthenticationService.initializeUser(), SettingsService.initializeSettings(), languageReady])
            .then(async () => {
                const preferredLocale = userStore.data?.preferences?.language || settingsStore.data?.default_locale;
                if (preferredLocale) await setCurrentLanguage(preferredLocale);

                // Personal theme preview (admins only). ?theme-preview=off is the
                // escape hatch when a broken preview makes the UI unusable — the
                // per-user analogue of pterodactyl:css_disabled.
                const themePreviewUrl = userStore.data?.themePreviewUrl;
                if (themePreviewUrl && new URLSearchParams(window.location.search).get('theme-preview') !== 'off') {
                    applyThemePreview(themePreviewUrl);
                }

                // Wait for the initial route's lazy chunk + middlewares to finish so the
                // real UI is mounted underneath before we fade out the skeleton. Avoids
                // the brief blank flash between unmounting the preloader and the route
                // component finishing its async load.
                await Router.isReady();

                ready.value = true;

                if (userStore.loggedIn) {
                    void import('~/api/services/client/notifications').then(({ default: NotificationsService }) =>
                        NotificationsService.initializeNotifications(),
                    );
                }
            })
            .catch((err) => {
                loadError.value = true;
                loadingMessage.value = 'Something went wrong. Try refreshing the page or come back later.';

                Logger.error('App', 'Initialization failed', err);
            });

        return {
            ready,
            loadError,
            loadingMessage,
        };
    },
});
</script>

<style>
.preloader-fade-leave-active {
    transition: opacity 0.25s ease-out;
}

.preloader-fade-leave-to {
    opacity: 0;
}
</style>
